import React, { useState } from 'react';
import styles from './CardDetails.module.css';
import { useSelector, useDispatch } from 'react-redux';
import EllipsesIcon from '../../../components/icons/EllipsesIcon';
import { Dropdown, Modal, Button, FloatingLabel, Form } from 'react-bootstrap';
import modalStyles from '../../../components/ModalStyles.module.css';
import useAxios from '../../../services/AxiosConfig';
import { showAlert } from '../../../components/PerinHealthAlertSlice';
import { setAlerts } from '../../../slicers/PatientSlice';
import { getAlertColorsAndIcon, alertPrioritySort, isItemActivelySilenced, getChartName, formatAlertDescription, getAlertTimestamp, getAlertSilenceTimestamp, getAlertDisplayName, formatCompletedDate } from '../../../utils/alertUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { v4 as uuidv4 } from "uuid";
import { setFilterItem } from '../Slices/PatientDetailFilterSlice';
import { setMenuItem } from '../Slices/PatientDetailSlice';
import { SmallGearIcon } from '../../../components/icons/Icons';
import AlarmOptionsModal from '../../../components/Alerts/AlarmOptionsModal';
import AlarmSettingsModal from '../../../components/Alerts/AlarmSettingsModal';
import { savePatientEnrollmentData } from '../../../utils/enrollmentUtils';
import { mergeModalitiesAndSerialNumbers } from '../../../views/patient-enrollment/SavedModalitiesSlicer';
import { setEnrollmentState, setFormValue } from '../../../views/patient-enrollment/PatientEnrollmentSlice';
import { useNavigate } from 'react-router-dom';

const AlarmDetails = ({ type }) => {

    const { id, dateFormat, fullName, unitType } = useSelector((state) => state.userInfo);
    const careTeam = useSelector((state) => state.patient.careTeam);
    const patient = useSelector((state) => state.patient);
    const alerts = patient.alerts.active[type];
    const navigate = useNavigate();
    const [alertModal, setAlertModal] = useState({
        open: false,
        alert: {}
    });
    const [optionsModal, setOptionsModal] = useState({
        open: false,
        alert: {}
    })
    const [settingsModal, setSettingsModal] = useState({
        open: false,
        alert: {}
    })
    const [confirmationModal, setConfirmationModal] = useState({
        open: false,
        alert: {},
        action: '',
        duration: null

    })
    const [escalatedProvider, setEscaltedProvider] = useState(0);
    const [showSilencedAlerts, setShowSilencedAlerts] = useState(false);
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);

    const filterAlerts = () => {
        const silencedAlerts = [];
        const activeAlerts = [];

        for (const item of alerts) {
            if (isItemActivelySilenced(item)) {
                silencedAlerts.push(item);
            } else {
                activeAlerts.push(item);
            }
        }

        return { silencedAlerts, activeAlerts };
    };

    const { activeAlerts, silencedAlerts } = filterAlerts();

    const onCloseOptionsModal = () => {
        setOptionsModal({
            open: false,
            alert: {}
        })
    };

    const onCloseSettingsModal = () => {
        setSettingsModal({
            open: false,
            alert: {}
        })
    };

    const onCloseConfirmationModal = () => {
        setEscaltedProvider(0);
        setConfirmationModal({
            open: false,
            alert: {},
            action: '',
            duration: null
        })
    };

    const openGraph = (alert) => {
        let alertStartDate = new Date(alert.createdDate);
        alertStartDate.setMinutes(alertStartDate.getMinutes() - 60);

        let alertEndDate = new Date(alert.createdDate);
        alertEndDate.setMinutes(alertEndDate.getMinutes() + 60);

        dispatch(setFilterItem({ key: "timePeriod", value: 'CustomPoint' }));
        dispatch(setFilterItem({ key: "startDate", value: alertStartDate.toISOString() }))
        dispatch(setFilterItem({ key: "endDate", value: alertEndDate.toISOString() }))
        if (alert.alert_modalityId) {
            const chartName = getChartName(alert.alert_modalityId);
            dispatch(setFilterItem({ key: "selectedScrollChart", value: chartName }))
        }
        dispatch(setMenuItem('Vitals'));
    };

    const handleUpdateEnrollment = async (alert) => {
        const ruleId = alert.alert_ruleId;
        const response = await axios.get(`enrollment/patient/${patient.id}`);
        const patientEnrollmentData = response.data?.data || [];
        if (patientEnrollmentData?.length === 0) {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "There was an error updating this program",
                    type: 'danger'
                })
            );
            return;
        }
        const { currentModalities, currentSerialNumbers } = savePatientEnrollmentData(patientEnrollmentData);
        dispatch(mergeModalitiesAndSerialNumbers({ modalities: currentModalities, serialNumbers: currentSerialNumbers }));
        let updatingInfo = null;
        const programWithRules = patientEnrollmentData.find((program) => program.devices.some((device) => device.rules.some((rule) => rule.ruleId === ruleId)));
        if (programWithRules) {
            updatingInfo = programWithRules;
        } else {
            updatingInfo = patientEnrollmentData[0];
        }
        dispatch(setEnrollmentState(updatingInfo));
        dispatch(setFormValue({key:"updatingProgramOptions", value:patientEnrollmentData}));
        navigate('/enroll-patient');
    }

    const handleAlertAction = async (alert, action, duration = null, durationDisplay = "") => {
        onCloseOptionsModal();
        onCloseSettingsModal();
        if (['report', 'document'].includes(action)) {
            return;
        }
        if (action === 'viewOnGraph') {
            openGraph(alert);
            return;
        }
        if (action === 'updateEnrollment') {
            handleUpdateEnrollment(alert);
            return;
        }
        let message = "Are you sure you want to clear this alarm?";
        let title = "Clear Alarm"
        if (action === 'silence') {
            message = `Are you sure you want to silence this alarm for ${durationDisplay}`
            title = "Silence Alarm"
        }
        if (action === 'unsilence') {
            message = `Are you sure you want to unsilence this alarm?`
            title = "Unsilence Alarm"
        }
        if (action === 'escalate') {
            message = `This will assign a task to another provider to review alarm`
            title = "Escalate Alarm"
        }
        setConfirmationModal({
            open: true,
            alert: alert,
            action: action,
            duration: duration,
            message: message,
            title: title
        })
    };

    const submitAlertAction = async (alert, action, duration = null) => {
        onCloseConfirmationModal()
        try {
            // Initialize baseAlertActions with existing actions
            const baseAlertActions = {
                report: { L: [] },
                clearAlert: { L: [] },
                silenceAlert: { L: [] },
                escalate: { L: [] },
                document: { L: [] }
            };

            // First, preserve all existing actions
            if (alert.alert_actions) {
                Object.keys(alert.alert_actions).forEach(actionType => {
                    const existingActions = alert.alert_actions[actionType];
                    if (existingActions.L) {
                        // If it's already in DynamoDB format with L property
                        baseAlertActions[actionType].L = [...existingActions.L];
                    } else if (Array.isArray(existingActions)) {
                        // If it's a plain array, convert to DynamoDB format
                        // Filter out any undefined or null values
                        baseAlertActions[actionType].L = existingActions
                            .filter(action => action && action.timestamp && action.userId)
                            .map(action => ({
                                M: {
                                    timestamp: { S: action.timestamp },
                                    userId: { N: action.userId.toString() },
                                    ...(action.duration && { duration: { N: action.duration.toString() } })
                                }
                            }));
                    }
                });
            }

            // Map the action to the correct key in alert_actions
            const actionMap = {
                'report': 'report',
                'clear': 'clearAlert',
                'clearAndResolveTask': 'clearAlert',
                'silence': 'silenceAlert',
                'unsilence': 'silenceAlert',
                'escalate': 'escalate',
                'document': 'document'
            };

            const status = action.includes('clear') ? 'resolved' : 'active';

            // Create the action data in DynamoDB format
            const actionData = {
                M: {
                    timestamp: { S: new Date().toISOString() },
                    userId: { N: id.toString() }
                }
            };

            // Add duration if it exists (for silence action)
            if (duration) {
                actionData.M.duration = { N: duration.toString() };
            }

            // Add the new action to the list
            baseAlertActions[actionMap[action]].L.push(actionData);

            const alertData = {
                accountId: parseInt(alert.alert_accountId),
                careTeam_accountId: id,
                alertId: alert.alertId,
                createdDate: alert.createdDate,
                status: status,
                alert_actions: baseAlertActions
            };

            if (duration) {
                alertData['silenceTimestamp'] = getAlertSilenceTimestamp(duration);
            }

            if (action === 'unsilence') {
                //change to now
                alertData['silenceTimestamp'] = getAlertSilenceTimestamp(0);
            }

            if (action === 'escalate') {
                try {
                    const organizationId = Array.isArray(patient.groups) && patient.groups.length > 0 && patient.groups[0]?.organizationId
                    ? patient.groups[0].organizationId
                    : 0;
                    const staffTask = {
                        accountId: Number(escalatedProvider),
                        staffTaskId: uuidv4(),
                        dueDate: '',
                        completedDate: '',
                        createdDate: new Date().toISOString(),
                        description: '',
                        newStatus: true,
                        details: {
                            patientDetails: {
                                patientId: patient.id,
                                patientFirstName: patient.firstName,
                                patientLastName: patient.lastName,
                            },
                            alertDetails: {
                                alertId: alert.alertId,
                                escalateAccountId: id
                            }
                        },
                        alertId: alert.alertId,
                        status: 'Pending',
                        type: '',
                        config: {
                            moduleName: 'staff_task',
                            subModuleName: 'escalate',
                            organizationId: organizationId,
                            details: {
                                patientName: `${patient.firstName} ${patient.lastName}`,
                                staffName: `${fullName}`,
                                alertName: alert.alert_name
                            }
                        }
                    };
                   
                    const parameters = [staffTask];

                    const taskResponse = await axios.request({
                        method: 'POST',
                        headers: { "Content-Type": "application/json" },
                        url: '/staffTasks/upload',
                        data: parameters  
                      });
                } catch (taskError) {
                    console.error("Error uploading staff task:", taskError);
                    dispatch(
                        showAlert({
                            header: "Error",
                            message: `Failed to create staff task while escalating alarm`,
                            type: 'danger'
                        })
                    );
                    setEscaltedProvider(0);
                    return;
                }
            }

           
            const response = await axios.request({
                method: 'PUT',
                headers: { "Content-Type": "application/json" },
                url: '/alerts',
                data: JSON.stringify(alertData)
            });

            if (response) {
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Alarm ${action} successful`,
                        type: 'success'
                    })
                );
                if (action === 'clearAndResolveTask') {
                    // Add safe checks for data existence
                    let calibrationTask;
                    try {
                        const taskResponse = await axios.get(`/tasks/list/${patient?.id}`);
                        const calibrationTasks = taskResponse?.data?.data?.upcoming?.calibrations;
                        if (Array.isArray(calibrationTasks) && calibrationTasks.length > 0) {
                            calibrationTask = calibrationTasks[0];
                        }
                    } catch (err) {
                        console.error('Error getting calibration tasks:', err);
                    }


                        try {
                            await axios.request({
                                method: 'PUT',
                                headers: { "Content-Type": "application/json" },
                                url: `/tasks/calibrations`,
                                data: JSON.stringify({
                                    accountId: patient.id,
                                    dueDate_taskType_id: calibrationTask.dueDate_taskType_id,
                                    dueDate: calibrationTask.dueDate,
                                    taskName: calibrationTask.name,
                                    taskDescription: calibrationTask.description,
                                    taskStatus: 'completed',
                                })
                            });
                        } catch (submitErr) {
                            console.error("Error submitting calibration task:", submitErr);
                        }
                }

                const alertResponse = await axios.get(`alerts/${patient.id}`);
                if (Array.isArray(alertResponse.data.data)) {
                    dispatch(setAlerts(alertResponse.data.data));
                }
            }
        } catch (error) {
            console.error("Error updating alert:", error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: `Failed to ${action} alarm`,
                    type: 'danger'
                })
            );
        }
        setEscaltedProvider(0);
    };

    return (
        <div className={styles.DetailsContainer}>
            {alerts.length === 0 &&
                <div>{`No active ${type} alarms found`}</div>
            }
            {[...activeAlerts].sort(alertPrioritySort).map((alert, index) => {
                let { icon, color, background } = getAlertColorsAndIcon(alert);
                return (
                    <div
                        key={alert.alertId}
                        className={`${styles.AlertContainer} ${index !== activeAlerts.length - 1 ? styles.AlertBorderBottom : ''} ${alert.alertId === patient.taskAlertId ? styles.SelectedAlertBackground : ''}`}>
                        <div className={styles.AlertTitleContainer}>
                            {icon}
                            <span className="subtitle-1" style={{ padding: "0 5px", textAlign: "start" }}>
                                {getAlertDisplayName(alert)}
                            </span>
                            {formatCompletedDate(alert, dateFormat)}
                            <div style={{ flex: 1 }}></div>
                            <button className={styles.optionsButton} onClick={() => setOptionsModal({ open: true, "alert": alert })}>
                                Options
                            </button>
                            <button className={styles.alarmSettingsButton} onClick={() => setSettingsModal({ open: true, "alert": alert })}>
                                <SmallGearIcon />
                                Alarm Settings
                            </button>
                        </div>
                        <div className={styles.AlertBody}>
                            {formatAlertDescription(alert, unitType)}
                        </div>
                    </div>
                )
            })}
            {silencedAlerts.length > 0 &&
                <div>
                    {activeAlerts.length === 0 &&
                        <hr></hr>
                    }
                    <button
                        style={{
                            border: 'none',
                            background: 'transparent',
                            display: "flex",
                            alignItems: 'center',
                            columnGap: "10px",
                            paddingBottom: showSilencedAlerts ? '10px' : '0px'
                        }}
                        onClick={() => setShowSilencedAlerts(!showSilencedAlerts)}
                    >
                        <FontAwesomeIcon icon={`fa-solid fa-angle-${showSilencedAlerts ? "down" : "right"}`} />
                        <span className="body-2">Silenced</span>
                    </button>
                    {showSilencedAlerts &&
                        [...silencedAlerts].sort(alertPrioritySort).map((alert, index) => {
                            let { icon, color } = getAlertColorsAndIcon(alert);
                            return (
                                <div
                                    key={alert.alertId}
                                    className={`${styles.AlertContainer} ${index !== silencedAlerts.length - 1 ? styles.AlertBorderBottom : ''}`}>
                                    <div className={styles.AlertTitleContainer} style={{ background: 'lightgrey', borderRadius: "4px" }}>
                                        <button
                                            className={styles.AlertTitleButton}
                                            style={{ color: color }}
                                            onClick={() => setAlertModal({ open: true, "alert": alert })}
                                        >
                                            {icon}
                                            {alert.alert_name}
                                        </button>
                                        {formatCompletedDate(alert, dateFormat)}
                                        <div style={{ flex: 1 }}></div>
                                        <button className={styles.optionsButton} onClick={() => setOptionsModal({ open: true, "alert": alert })}>
                                            Options
                                        </button>
                                        <button className={styles.alarmSettingsButton} onClick={() => setSettingsModal({ open: true, "alert": alert })}>
                                            <SmallGearIcon />
                                            Alarm Settings
                                        </button>
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
            }

            <AlarmOptionsModal
                optionsModal={optionsModal}
                onClose={onCloseOptionsModal}
                handleAlertAction={handleAlertAction}
            />

            <AlarmSettingsModal
                settingsModal={settingsModal}
                onClose={onCloseSettingsModal}
                handleAlertAction={handleAlertAction}
            />

            {/* action confirmation modal */}
            <Modal
                show={confirmationModal.open}
                onHide={onCloseConfirmationModal}
                centered
            >
                <Modal.Header closeButton>
                    <div className="subtitle-1">
                        {confirmationModal.title}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {confirmationModal.message}
                    {confirmationModal.action === 'escalate' &&
                        <>
                            <div style={{ padding: "30px 0 10px 0" }}>Please select an authorized provider</div>
                            <FloatingLabel label="Provider*">
                                <Form.Select
                                    required
                                    type="select"
                                    value={escalatedProvider}
                                    className={`${styles.BorderedComponent}`}
                                    id="gender"
                                    onChange={(e) => setEscaltedProvider(e.target.value)}
                                    placeholder="Provider*"
                                    style={{ width: "300px" }}
                                >
                                    <option value="">Select a Provider</option>
                                    {careTeam.filter((provider) => provider.alertAuthorized && provider.id !== id).map((provider) => (
                                        <option key={provider.id} value={provider.id}>
                                            {`${provider.firstName} ${provider.lastName}`}
                                        </option>
                                    ))}
                                </Form.Select>
                            </FloatingLabel>
                        </>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" className={modalStyles.SecondaryButton} onClick={onCloseConfirmationModal}>
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        disabled={confirmationModal.action === 'escalate' && escalatedProvider === 0}
                        className={modalStyles.PrimaryButton}
                        onClick={() => submitAlertAction(confirmationModal.alert, confirmationModal.action, confirmationModal.duration)}
                    >
                        Confirm
                    </Button>

                </Modal.Footer>
            </Modal>
        </div>
    )
}
export default AlarmDetails;